import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from './ui/button';
import { Heart, User } from 'lucide-react';
import { toast } from 'sonner';
import { analytics } from '../lib/analytics';
import { CHECKINS_UPDATED_EVENT } from '../lib/cloud-sync';

export function RelationshipModeToggle() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'relationship' | 'single'>(() => {
    if (typeof localStorage === 'undefined') return 'relationship';
    return localStorage.getItem('tension_mode') === 'single' ? 'single' : 'relationship';
  });

  useEffect(() => {
    const m = localStorage.getItem('tension_mode');
    setMode(m === 'single' ? 'single' : 'relationship');
  }, []);

  const hasRelationshipData = (): boolean => {
    try {
      const raw = localStorage.getItem('tension_relationships');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) && parsed.length > 0;
    } catch {
      return false;
    }
  };

  const hasSinglesData = (): boolean => {
    return Boolean(localStorage.getItem('tension_compatibility_profile'));
  };

  const apply = (next: 'relationship' | 'single') => {
    if (next === mode) return;

    const confirmMessage =
      next === 'single'
        ? 'Switch to Singles mode?\n\nThis changes your experience to personal growth tools and alignment flows. Your existing relationship data is not deleted.'
        : 'Switch to Relationship mode?\n\nThis changes your experience to couples tracking and relationship check-ins. Your singles data is not deleted.';
    if (!window.confirm(confirmMessage)) return;

    localStorage.setItem('tension_mode', next);
    setMode(next);
    window.dispatchEvent(new CustomEvent(CHECKINS_UPDATED_EVENT));
    analytics.track('relationship_mode_changed', { mode: next, source: 'settings' });

    if (next === 'single') {
      toast.success('Singles mode enabled.');
      navigate(hasSinglesData() ? '/compatibility-profile' : '/singles-onboarding', { replace: true });
      return;
    }

    toast.success('Relationship mode enabled.');
    navigate(hasRelationshipData() ? '/home' : '/onboarding', { replace: true });
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
      <Button
        type="button"
        variant={mode === 'relationship' ? 'default' : 'outline'}
        className="justify-start gap-2"
        onClick={() => apply('relationship')}
      >
        <Heart className="h-4 w-4 shrink-0" aria-hidden />
        Couples / relationship tracking
      </Button>
      <Button
        type="button"
        variant={mode === 'single' ? 'default' : 'outline'}
        className="justify-start gap-2"
        onClick={() => apply('single')}
      >
        <User className="h-4 w-4 shrink-0" aria-hidden />
        Singles mode
      </Button>
    </div>
  );
}
